import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  Header,
  Pagination,
  PermissionsProvider,
  SearchBar,
  TableSkeleton,
  Tabs,
} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import type {TabItem} from '@/components/Tabs/types';
import {BusinessLiveAccessStatus} from '@/types';

import LiveAccessTable from './LiveAccessTable';
import ManageTemplatesModal from './ManageTemplatesModal';
import {useLiveAccessList} from './useLiveAccessList';

const TabContent = ({hook}: {hook: ReturnType<typeof useLiveAccessList>}) => {
  const renderList = () => {
    if (hook.isLoading) {
      return <TableSkeleton />;
    }

    if (hook.loadFailed) {
      return (
        <div className="py-8 text-center">
          <p
            data-cy="live-access-load-error"
            className="mb-3 text-sm text-red-600 dark:text-red-400"
          >
            Couldn&apos;t load live access requests. Please try again.
          </p>
          <Button
            variant="outline"
            colorScheme="cyan"
            dataCy="live-access-load-retry"
            onClick={() => hook.fetchList({page: hook.displayPage - 1})}
          >
            Try again
          </Button>
        </div>
      );
    }

    if (!hook.list.length) {
      return (
        <p
          data-cy="live-access-empty"
          className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center"
        >
          No live access requests found.
        </p>
      );
    }

    return (
      <LiveAccessTable
        items={hook.list}
        onChanged={() => hook.fetchList({page: hook.displayPage - 1})}
      />
    );
  };

  return (
    <>
      {renderList()}
      {/* Hidden rather than unmounted while there is nothing to page through:
          Pagination fetches page 0 when it mounts, and mounting it after each
          load sent a second, identical request. Mounted, its fetch lands in
          the same commit as the hook's and the hook drops it. Shown on the
          total, not this page's rows, so approving or rejecting the last row
          of a later page still leaves Previous to step back with. */}
      <div className={hook.total > 0 ? undefined : 'hidden'}>
        <Pagination
          onPageChange={hook.fetchList as PaginationProps['onPageChange']}
          total={hook.total}
          isPageReset={hook.isPageReset}
          currentPageOverride={hook.displayPage}
        />
      </div>
    </>
  );
};

const LiveAccessScreen = () => {
  const hook = useLiveAccessList();
  const [showTemplates, setShowTemplates] = useState(false);

  const [activeTabId, setActiveTabId] = useState<string>(
    new URLSearchParams(window.location.search).get('liveAccessTab') ??
      BusinessLiveAccessStatus.Requested,
  );

  const handleTabChange = (tab: TabItem) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('liveAccessTab', tab.id);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${searchParams.toString()}`,
    );
    setActiveTabId(tab.id);
    hook.setTabStatus(tab.id as BusinessLiveAccessStatus);
  };

  const tabs: TabItem[] = [
    {
      id: BusinessLiveAccessStatus.Requested,
      title: 'Pending',
      dataCy: 'live-access-tab-requested',
      content: <TabContent hook={hook} />,
    },
    {
      id: BusinessLiveAccessStatus.Approved,
      title: 'Approved',
      dataCy: 'live-access-tab-approved',
      content: <TabContent hook={hook} />,
    },
    {
      id: BusinessLiveAccessStatus.Rejected,
      title: 'Denied',
      dataCy: 'live-access-tab-rejected',
      content: <TabContent hook={hook} />,
    },
  ];

  return (
    <PermissionsProvider
      permission={AfriexAdminPermission.BusinessLiveAccessRead}
    >
      <main className="my-5 mx-4 md:mx-10">
        <div className="flex items-center justify-between">
          <Header title="Live Access Requests" dataCy="live-access-title" />
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessLiveAccessUpdate}
          >
            <Button
              colorScheme="cyan"
              dataCy="live-access-manage-templates"
              onClick={() => setShowTemplates(true)}
            >
              Manage default templates
            </Button>
          </PermissionsProvider>
        </div>

        <div className="my-4 max-w-md">
          <SearchBar
            dataCy="live-access"
            placeholder="Search by business name or email"
            handleSearch={hook.handleSearch}
            handleAutoComplete={hook.handleSearch}
            clearFilters={hook.clearFilters}
          />
        </div>

        {!hook.isSearchActive ? (
          <Tabs
            tabs={tabs}
            queryKey="liveAccessTab"
            activeTabId={activeTabId}
            onTabChange={handleTabChange}
          />
        ) : (
          <TabContent hook={hook} />
        )}

        {showTemplates && (
          <ManageTemplatesModal
            isOpen={showTemplates}
            onClose={() => setShowTemplates(false)}
          />
        )}
      </main>
    </PermissionsProvider>
  );
};

export default LiveAccessScreen;
